import React, { useState, useEffect } from 'react';
import { NavLink, useNavigate } from 'react-router-dom';
import {
  HomeIcon,
  UserGroupIcon,
  DocumentTextIcon,
  BriefcaseIcon,
  Cog6ToothIcon,
  ChartBarIcon,
  CalendarDaysIcon,
  ChevronLeftIcon,
  ChevronRightIcon,
  ShieldCheckIcon,
  UserPlusIcon,
} from '@heroicons/react/24/outline';
import clsx from 'clsx';
import biditLogo from '../../assets/bidit-logo.svg';
import { useAuth } from '../../contexts/AuthContext';
import { isFieldTech, landingPath } from '../../utils/isFieldTech';
import { isSuperAdmin } from '../admin/SuperAdminGuard';
import { can } from '../../utils/permissions';

const SIDEBAR_COLLAPSED_KEY = 'bidit_sidebar_collapsed';

// IT-R5-028: Final nav order.
const navigation = [
  { name: 'Dashboard', href: '/dashboard', icon: HomeIcon, shortcut: 'D' },
  { name: 'Customers', href: '/customers', icon: UserGroupIcon, shortcut: 'C' },
  { name: 'Quotes', href: '/quotes', icon: DocumentTextIcon, shortcut: 'Q' },
  { name: 'Jobs', href: '/jobs', icon: BriefcaseIcon, shortcut: 'J' },
  { name: 'Calendar', href: '/calendar', icon: CalendarDaysIcon, shortcut: 'K' },
  { name: 'Reports', href: '/reports', icon: ChartBarIcon, shortcut: 'R' },
  { name: 'Settings', href: '/settings', icon: Cog6ToothIcon, shortcut: 'S' },
];

// IDI-582 S1: the only nav entries a field tech sees.
const FIELD_TECH_NAV = ['/jobs', '/calendar'];

export default function Sidebar({ open, onClose }) {
  const [collapsed, setCollapsed] = useState(() => {
    try {
      return localStorage.getItem(SIDEBAR_COLLAPSED_KEY) === 'true';
    } catch {
      return false;
    }
  });

  useEffect(() => {
    try {
      localStorage.setItem(SIDEBAR_COLLAPSED_KEY, String(collapsed));
    } catch {
      // ignore
    }
  }, [collapsed]);

  const toggleCollapse = () => setCollapsed(prev => !prev);

  return (
    <>
      {/* Mobile overlay */}
      {open && (
        <div
          className="fixed inset-0 z-40 bg-black/30 lg:hidden"
          onClick={onClose}
          aria-hidden="true"
        />
      )}

      {/* Mobile sidebar */}
      <aside
        className={clsx(
          'fixed inset-y-0 left-0 z-50 w-64 transform transition-transform duration-200 ease-in-out lg:hidden',
          open ? 'translate-x-0' : '-translate-x-full'
        )}
        role="navigation"
        aria-label="Main navigation"
        style={{ backgroundColor: 'var(--sidebar-bg)', borderRight: '1px solid var(--sidebar-border)' }}
      >
        <SidebarContent onClose={onClose} collapsed={false} />
      </aside>

      {/* Desktop sidebar */}
      <aside
        className={clsx(
          'hidden lg:flex lg:flex-shrink-0 transition-all duration-200 ease-in-out',
          collapsed ? 'lg:w-16' : 'lg:w-64'
        )}
        role="navigation"
        aria-label="Main navigation"
        style={{ backgroundColor: 'var(--sidebar-bg)', borderRight: '1px solid var(--sidebar-border)' }}
      >
        <div className={clsx(
          'transition-all duration-200 ease-in-out',
          collapsed ? 'w-16' : 'w-64'
        )}>
          <SidebarContent
            collapsed={collapsed}
            onToggleCollapse={toggleCollapse}
          />
        </div>
      </aside>
    </>
  );
}

function SidebarContent({ onClose, collapsed, onToggleCollapse }) {
  const navigate = useNavigate();
  const { user } = useAuth();
  const showSuperAdmin = isSuperAdmin(user);
  // IDI-582 S1: a field tech's nav is Jobs and Calendar only.
  const navItems = (isFieldTech(user)
    ? navigation.filter((item) => FIELD_TECH_NAV.includes(item.href))
    : navigation
  ).filter((item) => item.href !== '/reports' || can(user, 'reports.view')); // IDI-762 D4

  const superAdminNav = [
    { name: 'Contractors', href: '/super-admin/dealers', icon: UserGroupIcon },
    { name: 'Add Account', href: '/super-admin/accounts/new', icon: UserPlusIcon },
    // §3b (2026-08-18): /super-admin/master-defaults renders correctly
    // but was unreachable from the rail. Filed on IDI-70 as a minor; this
    // is the one-line nav entry.
    { name: 'Master Defaults', href: '/super-admin/master-defaults', icon: Cog6ToothIcon },
    { name: 'Reports', href: '/super-admin/reports', icon: ChartBarIcon },
  ];

  return (
    <nav className="flex h-full flex-col">
      {/* Logo */}
      <div
        className={clsx(
          'flex h-16 items-center transition-all duration-200',
          collapsed ? 'px-3 justify-center' : 'px-6'
        )}
        style={{ borderBottom: '1px solid var(--sidebar-border)' }}
      >
        <button
          onClick={() => { navigate(landingPath(user)); if (onClose) onClose(); }}
          className="flex items-center transition-colors cursor-pointer"
          aria-label="Go to Dashboard"
        >
          <img
            src={biditLogo}
            alt="BIDIT"
            className={clsx(
              'transition-all duration-200',
              collapsed ? 'h-8 w-8' : 'h-10'
            )}
          />
        </button>
      </div>

      {/* Nav items */}
      <div className="flex-1 overflow-y-auto py-4 px-2">
        <ul className="space-y-1">
          {navItems.map((item) => (
            <li key={item.name}>
              <NavLink
                to={item.href}
                onClick={onClose}
                className={({ isActive }) =>
                  clsx(
                    'group flex items-center text-sm transition-colors relative focus-ring',
                    'min-h-[44px]',
                    collapsed ? 'justify-center px-2 py-2.5' : 'gap-3 px-3 py-2.5',
                  )
                }
                style={({ isActive }) => ({
                  backgroundColor: isActive ? 'var(--nav-active-bg)' : undefined,
                  borderLeft: isActive ? '3px solid var(--nav-active-border)' : '3px solid transparent',
                  borderRadius: 0,
                })}
                title={collapsed ? item.name : undefined}
              >
                {({ isActive }) => (
                  <>
                    <item.icon
                      className="h-5 w-5 flex-shrink-0"
                      style={{ color: isActive ? 'var(--color-action-primary)' : 'var(--sidebar-text-muted)' }}
                    />
                    {!collapsed && (
                      <span style={{
                        color: isActive ? 'white' : 'var(--sidebar-text-muted)',
                        fontWeight: isActive ? 700 : 400,
                      }}>
                        {item.name}
                      </span>
                    )}
                    {/* Tooltip on hover when collapsed */}
                    {collapsed && (
                      <span
                        className="absolute left-full ml-2 px-2 py-1 text-xs rounded opacity-0 group-hover:opacity-100 transition-opacity pointer-events-none whitespace-nowrap z-50"
                        style={{ backgroundColor: 'var(--neutral-900)', color: 'var(--neutral-50)' }}
                      >
                        {item.name}
                      </span>
                    )}
                  </>
                )}
              </NavLink>
            </li>
          ))}
        </ul>

        {showSuperAdmin && (
          <div className="mt-6">
            {!collapsed && (
              <p
                className="px-3 mb-1 text-xs font-semibold uppercase tracking-wide"
                style={{ color: 'var(--sidebar-text-muted)' }}
              >
                Super Admin
              </p>
            )}
            <ul className="space-y-1">
              {superAdminNav.map((item) => (
                <li key={item.name}>
                  <NavLink
                    to={item.href}
                    onClick={onClose}
                    className={() =>
                      clsx(
                        'group flex items-center text-sm transition-colors relative focus-ring',
                        'min-h-[44px]',
                        collapsed ? 'justify-center px-2 py-2.5' : 'gap-3 px-3 py-2.5',
                      )
                    }
                    style={({ isActive }) => ({
                      backgroundColor: isActive ? 'var(--nav-active-bg)' : undefined,
                      borderLeft: isActive ? '3px solid var(--nav-active-border)' : '3px solid transparent',
                      borderRadius: 0,
                    })}
                    title={collapsed ? item.name : undefined}
                  >
                    {({ isActive }) => (
                      <>
                        <ShieldCheckIcon
                          className="h-5 w-5 flex-shrink-0"
                          style={{ color: isActive ? 'var(--color-action-primary)' : 'var(--sidebar-text-muted)' }}
                        />
                        {!collapsed && (
                          <span style={{
                            color: isActive ? 'white' : 'var(--sidebar-text-muted)',
                            fontWeight: isActive ? 700 : 400,
                          }}>
                            {item.name}
                          </span>
                        )}
                      </>
                    )}
                  </NavLink>
                </li>
              ))}
            </ul>
          </div>
        )}
      </div>

      {/* Collapse toggle (desktop only) */}
      {onToggleCollapse && (
        <div className="p-2" style={{ borderTop: '1px solid var(--sidebar-border)' }}>
          <button
            onClick={onToggleCollapse}
            className={clsx(
              'flex items-center text-sm transition-colors w-full min-h-[40px] focus-ring',
              collapsed ? 'justify-center px-2' : 'gap-3 px-3'
            )}
            style={{ color: 'var(--sidebar-text-muted)' }}
            aria-label={collapsed ? 'Expand sidebar' : 'Collapse sidebar'}
            title={collapsed ? 'Expand sidebar' : 'Collapse sidebar'}
          >
            {collapsed ? (
              <ChevronRightIcon className="h-4 w-4" />
            ) : (
              <>
                <ChevronLeftIcon className="h-4 w-4" />
                <span>Collapse</span>
              </>
            )}
          </button>
        </div>
      )}
    </nav>
  );
}
